import { useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { FaExclamationCircle, FaClock } from 'react-icons/fa';
import { formatNumber } from "../utils/numberFormat";
import useIsMobile from '../hooks/useIsMobile';
import { formatPrezzoPane, prezzoPaneVisibile } from '../utils/mostraPrezziDestinazione';

const getOreBloccoProd = (row) => {
  const raw =
    row?.dati_articolo?.orebloccoprod ??
    row?.data_forn_arti?.orebloccoprod ??
    row?.orebloccoprod;
  const n = Number(String(raw ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const formatOreBloccoLabel = (ore) => {
  if (!Number.isFinite(ore)) return String(ore);
  return Number.isInteger(ore) ? String(ore) : String(ore).replace(/\.0+$/, '');
};



const AddArticleModal = ({
  showModal,
  setShowModal,
  addArticle, // Cambiato da selectedArticle a addArticle
  quantities = {}, // Fallback per evitare undefined
  setQuantities,
  handleSaveArticle, // Funzione per salvare l'articolo
  currentDay,
  unitaMisura,
  darkMode,
  readOnlyWeekdayKeys = [],
  getExtraReadOnlyKeysForArticle = null,
  mostraPrezzi = false,
}) => {
  const isMobile = useIsMobile();
  const daysOfWeek = ["lunedi", "martedi", "mercoledi", "giovedi", "venerdi", "sabato", "domenica"];

  const extraReadOnlyFromProd = useMemo(() => {
    if (typeof getExtraReadOnlyKeysForArticle !== 'function' || !addArticle) return [];
    const extra = getExtraReadOnlyKeysForArticle(addArticle);
    return Array.isArray(extra) ? extra : [];
  }, [getExtraReadOnlyKeysForArticle, addArticle]);

  const mergedReadOnlyWeekdayKeys = useMemo(() => {
    const base = Array.isArray(readOnlyWeekdayKeys) ? readOnlyWeekdayKeys : [];
    return [...new Set([...base, ...extraReadOnlyFromProd])];
  }, [readOnlyWeekdayKeys, extraReadOnlyFromProd]);

  const oreBloccoProdArticolo = addArticle ? getOreBloccoProd(addArticle) : 0;

  // Inizializza le quantità: giorno corrente con ordine minimo, gli altri a 0
  const initializeQuantities = () => {
    const base = {
      lunedi: 0,
      martedi: 0,
      mercoledi: 0,
      giovedi: 0,
      venerdi: 0,
      sabato: 0,
      domenica: 0,
    };
    if (
      addArticle &&
      currentDay &&
      daysOfWeek.includes(currentDay) &&
      !mergedReadOnlyWeekdayKeys.includes(currentDay)
    ) {
      const isKg = String(addArticle.unita || '').toLowerCase() === 'kg';
      let range = Number(addArticle.qtaRange) || 0;
      if (range === 0) range = isKg ? 0.1 : 1;
      base[currentDay] = Number(addArticle.qtaMinima) || range;
    }
    setQuantities(base);
  };

  const handleQuantityChange = (day, value) => {
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [day]: Number(value),
    }));
  };

  const handleSave = () => {
    handleSaveArticle(addArticle, quantities); // Chiama la funzione di salvataggio
    setShowModal(false); // Chiude il modale
  };


  const handleCancel = () => {
    setShowModal(false); // Chiude il modale
  };

  return (
    <Modal 
      show={showModal} 
      onHide={handleCancel}
      centered
      fullscreen={isMobile || undefined}
      onShow={initializeQuantities}
    >
      <Modal.Header closeButton className={darkMode ? 'bg-dark text-light' : ''}>
        <Modal.Title>Aggiungi articolo</Modal.Title>
      </Modal.Header>
      <Modal.Body className={darkMode ? 'bg-dark text-light' : ''}>
        {addArticle && (
          <>
            <div className="mb-3">
              <p><strong>Codice prodotto:</strong> {addArticle.codice}</p>
              <p><strong>Descrizione:</strong> {addArticle.descriz}</p>
              <p><strong>Unità di misura:</strong> {addArticle.unita || unitaMisura}</p>
              {mostraPrezzi && prezzoPaneVisibile(addArticle.prezzo) && (
                <p>
                  <strong>Prezzo:</strong>{' '}
                  {formatPrezzoPane(addArticle.prezzo, addArticle.unita || addArticle.unitaMisura || unitaMisura)}
                </p>
              )}
              {addArticle.immagine && (
                <div className="mb-3 text-center">
                  <img 
                    src={addArticle.immagine} 
                    alt={addArticle.descrizione}
                    style={{ maxHeight: '100px', maxWidth: '150px' }} 
                    className="img-thumbnail"
                  />
                </div>
              )}
              {addArticle.schedaTecnica && (
                <p><strong>Scheda tecnica:</strong> {addArticle.schedaTecnica}</p>
              )}
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Quantità per i giorni della settimana:</Form.Label>
              {oreBloccoProdArticolo > 0 && (
                <Alert
                  variant={darkMode ? 'dark' : 'light'}
                  className="py-2 px-3 small mb-3 border border-danger border-opacity-50"
                >
                  <div className="d-flex align-items-start gap-2">
                    <FaClock className="text-danger mt-1 flex-shrink-0" aria-hidden />
                    <span>
                      Il prodotto prevede la modifica dell&apos;ordine{' '}
                      <strong className="text-danger">{formatOreBloccoLabel(oreBloccoProdArticolo)}</strong>
                      {' '}ore prima del lancio di produzione.
                    </span>
                  </div>
                </Alert>
              )}
              <Row>
                {daysOfWeek.map((day) => {
  
                  // Calcolo dei parametri per la select
                    const isKg = (addArticle.unita?.toLowerCase() === "kg");
                    let range = Number(addArticle.qtaRange) || 0;
                   
                    if (range === 0) range = isKg ? 0.1 : 1;
                      const min = Number(addArticle.qtaMinima) || range;
                      const max = Number(addArticle.qtaMax) || (isKg ? 100 : 1000); // valore massimo arbitrario
                    // Genera i valori disponibili
                    let values = [];
                    for (let v = min; v <= max; v = +(v + range).toFixed(3)) {
                      values.push(isKg ? v.toFixed(3) : v.toString());
                    }

                    const lockedByOreProd = extraReadOnlyFromProd.includes(day);
                    const lockedByFascia =
                      Array.isArray(readOnlyWeekdayKeys) && readOnlyWeekdayKeys.includes(day);
                    const bloccoProdGiorno = lockedByOreProd || lockedByFascia;
                    const bloccoProdTitle = lockedByOreProd
                      ? "Troppo tardi per modificare l'ordine per questo giorno (tempo minimo di produzione)"
                      : "In questa fascia oraria non è consentito modificare le quantità per domani.";
                    const bloccoProdAria = lockedByOreProd
                      ? "Troppo tardi per modificare l'ordine per questo giorno"
                      : "Modifiche non consentite per domani in fascia oraria";

                    return (
                      <Col key={day} xs={6} md={4} className="mb-2">
                        <Form.Text
                          className={`d-flex align-items-center gap-1 flex-wrap ${
                            day === currentDay ? 'text-muted fw-bold' : 'text-muted'
                          }`}
                        >
                          <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                          {bloccoProdGiorno && (
                            <FaExclamationCircle
                              className="text-danger flex-shrink-0"
                              style={{ fontSize: '1rem' }}
                              title={bloccoProdTitle}
                              aria-label={bloccoProdAria}
                            />
                          )}
                        </Form.Text>

                        <Form.Select
                          value={(() => {
                            const q = Number(quantities?.[day] || 0);
                            if (!isKg) return quantities?.[day] ?? 0;
                            return q === 0 ? '0' : q.toFixed(3);
                          })()}
                          onChange={e => handleQuantityChange(day, Number(e.target.value.replace(',', '.')))}
                          disabled={mergedReadOnlyWeekdayKeys.includes(day)}
                          className={day === currentDay ? "bg-primary text-light" : ""}>
                          <option value={0}>0</option>
                          {values.map(val => {
                             let conversionText = "";
                             const peso = Number(addArticle.peso) || 0;
                             const numVal = Number(val);
 
                             if (peso > 0 && numVal > 0) {
                               if (isKg) {
                                 const pezzi = (numVal / peso).toFixed(0);
                                 conversionText = ` (≈ ${formatNumber(pezzi)} pz)`;
                               } else {
                                 const kg = (numVal * peso).toFixed(3);
                                 conversionText = ` (≈ ${formatNumber(kg)} kg)`;
                               }
                             }

                            return val !== "0" && val !== 0 && (
                              <option key={val} value={val}>
                                {isKg ? formatNumber(val, 3) : val}{conversionText}
                              </option>
                            )
                          }
                          )}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {isKg && addArticle.peso && isFinite(quantities?.[day] / addArticle.peso) ? (
                            ` (${formatNumber(quantities?.[day] / addArticle.peso , 0)} Pz)`
                          ) : !isKg && addArticle.peso && (quantities?.[day] * addArticle.peso !== 0) ? (
                            ` (${formatNumber(quantities?.[day] * addArticle.peso, 3)} Kg)`
                          ) : null}
                        </Form.Text>
                      </Col>
                    );
                })}
              </Row>
            </Form.Group>

            <div className="mt-3">
              <p className="text-muted">
                {(() => {
                  const unita = addArticle.unita || unitaMisura;
                  const isKgUnit = unita && unita.toLowerCase() === "kg";
                  console.log("Unità:", unita, "isKg:", isKgUnit); // Debug
                  
                  return isKgUnit ? 
                    "Le quantità di questo articolo sono a peso. Il calcolo peso/pezzi è solamente indicativo." :
                    "Le quantità di questo articolo sono a Pz. Il calcolo del peso è solamente indicativo.";
                })()}
              </p>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className={darkMode ? 'bg-dark text-light' : ''}>
        <Button 
          variant="secondary" 
          onClick={handleCancel}
        >
          Annulla
        </Button>
        <Button 
          variant="success" 
          onClick={handleSave}
        >
          <i className="fas fa-save me-1"></i> Salva
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddArticleModal;